import React, { useState } from "react";
import { Link, useLocation, useParams, matchPath } from "react-router-dom";
import { useTheme } from '@mui/material/styles';
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Stack,
    Button
} from "@mui/material";

import { Logout, AutoStories } from "@mui/icons-material";
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Keeping if needed, but maybe cleaner without

// Logos
import CloudContentLogoWithText from "../../../assets/images/cc_logo_only.png"; // Assuming this is the full logo
// import CloudContentLogoOnly from "../../../assets/images/cc_logo_only.png";

// Icons
import MediaIcon from "../../../components/icons/MediaIcon";
import SettingsIcon from "../../../components/icons/SettingsIcon";
import ContentIcon from "../../../components/icons/ContentIcon";
import ContentModelIcon from "../../../components/icons/ContentModelIcon";
import WorkspaceIcon from "../../../components/icons/WorkspaceIcon";
import LibraryIcon from "../../../components/icons/LibraryIcon";
import DashboardIcon from "../../../components/icons/DashboardIcon";
import ProfileIcon from "../../../assets/images/profile_circle.png";

import AuthLogout from "../../../pages/authentication/auth-forms/AuthLogout";
import EntryQuickSwitcher from "./EntryQuickSwitcher";
import EnvironmentSwitcher from "./EnvironmentSwitcher";

const TopBar = () => {
    const { workspaceId } = useParams();
    const location = useLocation();
    const theme = useTheme();

    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    const name = currentUser?.name ? currentUser?.name?.split(' ')[0] : '';
    const avatarUrl = currentUser?.avatarUrl || ProfileIcon;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Check if we are on the Edit Entry page
    const isEditEntryPage = matchPath(
        { path: "/:workspaceId/edit-entry/:id", end: true },
        location.pathname
    );

    // Navigation Items
    const menuItems = [
        {
            text: "Dashboard",
            icon: <DashboardIcon />,
            path: `/${workspaceId}/dashboard`,
        },
        {
            text: "Content model",
            icon: <ContentModelIcon />,
            path: `/${workspaceId}/content-models`,
        },
        {
            text: "Content",
            icon: <ContentIcon />,
            path: `/${workspaceId}/entries`,
        },
        {
            text: "Media",
            icon: <MediaIcon />,
            path: `/${workspaceId}/media-list`,
        },
        // {
        //     text: "Prompt Library",
        //     icon: <LibraryIcon />,
        //     path: `/${workspaceId}/prompt-library`
        // },
        {
            text: "Settings",
            icon: <SettingsIcon />,
            path: `/${workspaceId}/settings`,
        },
    ];

    const primaryMenuItems = [
        {
            text: "Workspace",
            icon: <WorkspaceIcon />,
            path: `/space`,
        },
        {
            text: "Apps",
            icon: <ContentIcon />,
            path: `/apps`,
        },
        {
            text: "OAuth Clients",
            icon: <ContentIcon />,
            path: `/oauth-clients`,
        },
        {
            text: "Documentation",
            icon: <AutoStories />,
            path: `/documentation`,
        },
    ];

    // Determine which menu to show
    const isPrimaryView = ['/space', '/apps', '/oauth-clients', '/documentation'].includes(location.pathname);
    const currentMenuItems = isPrimaryView ? primaryMenuItems : menuItems;

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                width: '100%',
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                zIndex: theme.zIndex.appBar,
                color: '#000'
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: '70px !important' }}>
                {/* Left: Logo + environment */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 3 }}>
                    <img
                        src={CloudContentLogoWithText}
                        alt="Cloud Content"
                        style={{ height: '40px', objectFit: 'contain' }}
                    />
                    {/* Environments belong to a workspace, so the switcher only
                        appears once the user is inside one. */}
                    {!isPrimaryView && workspaceId && <EnvironmentSwitcher />}
                </Box>

                {/* Center: Navigation or Entry Switcher */}
                <Box sx={{ display: 'flex', flexGrow: 1, gap: 1, overflowX: 'auto', alignItems: "center" }}>
                    {currentMenuItems.map((item) => {
                        const isSelected = location.pathname === item.path;
                        return (
                            <Button
                                key={item.text}
                                component={Link}
                                to={item.path}
                                startIcon={
                                    <Box component="span" sx={{
                                        color: isSelected ? "#4f46e5" : "#64748b",
                                        display: 'flex',
                                        '& svg': { fontSize: '20px' }
                                    }}>
                                        {item.icon}
                                    </Box>
                                }
                                sx={{
                                    textTransform: 'none',
                                    color: isSelected ? "#4f46e5" : "#64748b",
                                    backgroundColor: isSelected ? "rgba(99, 102, 241, 0.08)" : "transparent",
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1,
                                    fontWeight: isSelected ? 600 : 500,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        backgroundColor: isSelected ? "rgba(99, 102, 241, 0.12)" : "rgba(241, 245, 249, 0.8)",
                                        color: isSelected ? "#4f46e5" : "#1e293b",
                                    }
                                }}
                            >
                                {item.text}
                            </Button>
                        );
                    })}

                    {/* Quick Switcher for Edit Entry Page */}
                    {isEditEntryPage && (
                        <Box sx={{ ml: 2 }}>
                            <EntryQuickSwitcher />
                        </Box>
                    )}

                    {/* Back to Workspaces Link (only when not in primary view) */}
                    {!isPrimaryView && (
                        <>
                            <Box sx={{ width: '1px', height: '24px', bgcolor: 'divider', mx: 1 }} />
                            <Button
                                component={Link}
                                to="/space"
                                startIcon={<WorkspaceIcon />}
                                sx={{
                                    textTransform: 'none',
                                    color: "#64748b",
                                    borderRadius: '12px',
                                    px: 2,
                                    '&:hover': {
                                        backgroundColor: "rgba(241, 245, 249, 0.8)",
                                        color: "#1e293b",
                                    }
                                }}
                            >
                                Workspaces
                            </Button>
                        </>
                    )}
                </Box>

                {/* Right: User Profile */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <Box
                        onClick={handleMenuClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            p: 0.5,
                            pr: 1.5,
                            borderRadius: '24px',
                            border: '1px solid transparent',
                            '&:hover': {
                                backgroundColor: "rgba(241, 245, 249, 0.8)",
                                borderColor: "rgba(226, 232, 240, 0.8)"
                            }
                        }}
                    >
                        <Avatar
                            alt={name}
                            src={avatarUrl}
                            sx={{ width: 36, height: 36 }}
                        />
                        <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', md: 'block' } }}>
                            {name}
                        </Typography>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                                mt: 1.5,
                                borderRadius: '12px',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem disableRipple>
                            <AuthLogout>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Logout fontSize="small" />
                                    <Typography variant="body2">Log out</Typography>
                                </Stack>
                            </AuthLogout>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;
